import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inp, L, Check } from "./inputs";

const Card = ({ title, sub, children }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6 space-y-4">
    <div><h2 className="font-display font-bold text-lg">{title}</h2>{sub && <p className="text-sm text-white/40 mt-1">{sub}</p>}</div>
    {children}
  </div>
);

export const CommerceSettingsTab = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/commerce-settings").then((r) => setS(r.data)).catch(() => toast.error("Failed to load settings")); }, []);
  if (!s) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div>;

  const fee = s.service_fee || {}; const tax = s.tax || {}; const ship = s.shipping || {};
  const setFee = (k, v) => setS({ ...s, service_fee: { ...fee, [k]: v } });
  const setTax = (k, v) => setS({ ...s, tax: { ...tax, [k]: v } });
  const setShip = (k, v) => setS({ ...s, shipping: { ...ship, [k]: v } });
  const toggleApplies = (k) => {
    const cur = fee.applies_to || [];
    setFee("applies_to", cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...s, service_fee: { ...fee, amount: Number(fee.amount || 0) }, tax: { ...tax, rate_pct: Number(tax.rate_pct || 0) }, shipping: { ...ship, flat_rate: Number(ship.flat_rate || 0), free_over: ship.free_over === "" || ship.free_over === null ? null : Number(ship.free_over) } };
      await api.put("/admin/commerce-settings", body);
      toast.success("Settings saved — checkout updates instantly");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="settings-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-2xl">Pricing, Fees & Tax</h1>
        <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="rounded-full bg-[#E63946] px-6 py-2.5 text-sm hover:bg-[#BA1B25] disabled:opacity-60">{saving ? "Saving…" : "Save Settings"}</button>
      </div>

      <Card title="Service / Convenience Fee" sub="Optional fee added at checkout. It is always clearly shown to the customer before payment.">
        <Check label="Charge a service fee" checked={fee.enabled} onChange={(v) => setFee("enabled", v)} testid="fee-enabled-check" />
        <div className="grid sm:grid-cols-3 gap-4">
          <div><L>Fee Type</L>
            <select className={inp} value={fee.mode || "flat_order"} onChange={(e) => setFee("mode", e.target.value)} data-testid="fee-mode-select">
              <option value="flat_order">Flat fee per order</option>
              <option value="percent">Percentage of order</option>
              <option value="per_item">Flat fee per item</option>
              <option value="per_ticket">Flat fee per ticket</option>
            </select>
          </div>
          <div><L>{fee.mode === "percent" ? "Percentage (%)" : "Amount ($)"}</L><input type="number" step="0.01" className={inp} data-testid="fee-amount-input" value={fee.amount ?? ""} onChange={(e) => setFee("amount", e.target.value)} /></div>
          <div><L>Label shown to customer</L><input className={inp} value={fee.label || "Service Fee"} onChange={(e) => setFee("label", e.target.value)} /></div>
        </div>
        <div className="flex gap-6">
          <Check label="Apply to store purchases" checked={(fee.applies_to || []).includes("store")} onChange={() => toggleApplies("store")} />
          <Check label="Apply to event tickets" checked={(fee.applies_to || []).includes("tickets")} onChange={() => toggleApplies("tickets")} />
        </div>
        <p className="text-xs text-white/35">Donations are never charged a service fee — Giving stays completely separate from the store.</p>
      </Card>

      <Card title="Sales Tax" sub="Configure the Arkansas state + local rate you collect. Mark items taxable/non-taxable on each product or event.">
        <Check label="Automatically calculate sales tax at checkout" checked={tax.enabled} onChange={(v) => setTax("enabled", v)} testid="tax-enabled-check" />
        <div className="grid sm:grid-cols-3 gap-4">
          <div><L>Tax Rate (%)</L><input type="number" step="0.001" className={inp} data-testid="tax-rate-input" value={tax.rate_pct ?? ""} onChange={(e) => setTax("rate_pct", e.target.value)} /></div>
          <div className="flex items-end pb-1"><Check label="Tax shipping" checked={tax.tax_shipping} onChange={(v) => setTax("tax_shipping", v)} /></div>
          <div className="flex items-end pb-1"><Check label="Tax service fees" checked={tax.tax_fees} onChange={(v) => setTax("tax_fees", v)} /></div>
        </div>
        <p className="text-xs text-white/35">Little Rock combined rate is currently about 8.625% (AR 6.5% + Pulaski County + city) — verify current rates with the AR DFA. Tax collected is tracked separately in Reports. Donations are never taxed.</p>
      </Card>

      <Card title="Shipping" sub="Applied only when an order contains merchandise that requires shipping.">
        <Check label="Charge shipping" checked={ship.enabled} onChange={(v) => setShip("enabled", v)} />
        <div className="grid sm:grid-cols-2 gap-4">
          <div><L>Flat Rate ($ per order)</L><input type="number" step="0.01" className={inp} value={ship.flat_rate ?? ""} onChange={(e) => setShip("flat_rate", e.target.value)} /></div>
          <div><L>Free shipping over ($ — blank for never)</L><input type="number" step="0.01" className={inp} value={ship.free_over ?? ""} onChange={(e) => setShip("free_over", e.target.value)} /></div>
        </div>
      </Card>
    </div>
  );
};
